import React, { useRef } from 'react';
import { useImpression, useTracker } from '@archipro-website/tracker';
import { trackEnquiryEngagement } from '~/modules/tracking/util/trackEnquiryEngagement';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import { EnquiryForm, type EnquiryFormProps } from '../EnquiryForm';

interface EnquiryInlineProps {
    className?: string;
    /** The professional to submit an enquiry to. */
    professional: EnquiryFormProps['professional'];
    /** The type of Library Item to enquire about (professional, product, project, article). */
    itemType: EnquiryFormProps['itemType'];
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: number;
    /** The subject of the enquiry (product, project, article). */
    relatedItem?: EnquiryFormProps['relatedItem'];
    /** Include the branch dropdown in the enquiry form if the professional has branches. */
    includeBranch?: boolean;
    /** Shape of the attached-file chips. The revamped surfaces pass `compact`. */
    attachmentVariant?: EnquiryFormProps['attachmentVariant'];
    /** Ask for a suburb / postcode. Defaults to `true`; the revamped surfaces opt out. */
    collectLocation?: boolean;
    /** The text to show on the submit button. */
    submitButtonText?: string;
    /** Show custom enquiry text from professional settings. */
    showCustomEnquiryText?: boolean;
    /** Disable the submit button. */
    disableSubmit?: boolean;
    /** The name of the item being enquired about (e.g. professional name). Used to pre-fill the message. */
    itemTitle?: string;
    /** The CTA label. Used to tailor the pre-filled message. */
    ctaLabel?: string;
}

/**
 * Inline (in-page) version of the enquiry form.
 * @see {@link packages/web/app/modules/enquiry/component/EnquiryForm.tsx}
 */
export const EnquiryInline = ({
    className,
    professional,
    itemType,
    siteTreeID,
    relatedItem,
    includeBranch = false,
    collectLocation = true,
    attachmentVariant,
    submitButtonText,
    showCustomEnquiryText = true,
    disableSubmit = false,
    itemTitle,
    ctaLabel,
}: EnquiryInlineProps) => {
    const tracker = useTracker();
    const formRef = useRef<HTMLDivElement>(null);
    const isEngagementTracked = useRef(false);

    useImpression({
        ref: formRef,
        onImpression: async () => {
            trackEnquireReveal(tracker, {
                professionalName: professional.Title,
                itemID: siteTreeID,
                itemType,
                source: 'InPage',
                professionalID: professional.ID,
            });
        },
    });

    const trackEngagement = (e: React.FocusEvent<HTMLDivElement>) => {
        if (
            e.target instanceof HTMLInputElement ||
            e.target instanceof HTMLTextAreaElement ||
            e.target instanceof HTMLSelectElement
        ) {
            // Only track engagement once.
            if (!isEngagementTracked.current) {
                trackEnquiryEngagement(tracker, {
                    professional: {
                        ID: professional.ID,
                        Title: professional.Title,
                    },
                    itemID: siteTreeID,
                    itemType,
                    source: 'InPage',
                });
                isEngagementTracked.current = true;
            }
        }
    };
    return (
        <div ref={formRef} className={className} onFocus={trackEngagement}>
            <EnquiryForm
                professional={professional}
                itemType={itemType}
                siteTreeID={siteTreeID}
                source="InPage"
                relatedItem={relatedItem}
                includeBranch={includeBranch}
                collectLocation={collectLocation}
                attachmentVariant={attachmentVariant}
                isModal={false}
                showCustomEnquiryMessage={showCustomEnquiryText}
                submitButtonText={submitButtonText}
                disableSubmit={disableSubmit}
                showSuccessMessage
                itemTitle={itemTitle}
                ctaLabel={ctaLabel}
            />
        </div>
    );
};
